import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { differenceInDays, parseISO, isValid, isBefore, startOfDay } from 'date-fns';

export type WidgetMode = 'browse' | 'during-stay';

interface StayDates {
  startDate: string | null;
  endDate: string | null;
}

interface StayContextValue {
  /** Current stay dates */
  stayDates: StayDates;
  /** Widget display mode */
  mode: WidgetMode;
  /** Whether stay dates are set and valid */
  hasValidStayDates: boolean;
  /** Number of days until stay ends (for countdown) */
  daysUntilStayEnds: number | null;
  /** Number of days in the stay */
  stayDuration: number | null;
  /** Update stay dates */
  setStayDates: (startDate: string | null, endDate: string | null) => void;
  /** Update widget mode */
  setMode: (mode: WidgetMode) => void;
  /** Clear stay dates and reset to browse mode */
  clearStayDates: () => void;
}

const StayContext = createContext<StayContextValue | null>(null);

interface StayProviderProps {
  children: ReactNode;
}

/**
 * Validate ISO date string (YYYY-MM-DD)
 */
function isValidDateString(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;
  const date = parseISO(dateStr);
  return isValid(date);
}

/**
 * Get stay dates from URL params
 */
function getStayDatesFromUrl(searchParams: URLSearchParams): StayDates {
  const stayStart = searchParams.get('stayStart');
  const stayEnd = searchParams.get('stayEnd');

  return {
    startDate: isValidDateString(stayStart) ? stayStart : null,
    endDate: isValidDateString(stayEnd) ? stayEnd : null,
  };
}

/**
 * Get mode from URL params
 */
function getModeFromUrl(searchParams: URLSearchParams): WidgetMode {
  const mode = searchParams.get('mode');
  return mode === 'during-stay' ? 'during-stay' : 'browse';
}

export function StayProvider({ children }: StayProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL params
  const [stayDates, setStayDatesState] = useState<StayDates>(() =>
    getStayDatesFromUrl(searchParams)
  );
  const [mode, setModeState] = useState<WidgetMode>(() =>
    getModeFromUrl(searchParams)
  );

  // Listen for messages from parent window (embed script)
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;

      switch (data.type) {
        case 'el-update-stay-dates':
          setStayDatesState({
            startDate: isValidDateString(data.stayStart) ? data.stayStart : null,
            endDate: isValidDateString(data.stayEnd) ? data.stayEnd : null,
          });
          break;

        case 'el-update-mode':
          if (data.mode === 'browse' || data.mode === 'during-stay') {
            setModeState(data.mode);
          }
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Sync URL params when stay dates change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let changed = false;

    if (stayDates.startDate) {
      if (newParams.get('stayStart') !== stayDates.startDate) {
        newParams.set('stayStart', stayDates.startDate);
        changed = true;
      }
    } else if (newParams.has('stayStart')) {
      newParams.delete('stayStart');
      changed = true;
    }

    if (stayDates.endDate) {
      if (newParams.get('stayEnd') !== stayDates.endDate) {
        newParams.set('stayEnd', stayDates.endDate);
        changed = true;
      }
    } else if (newParams.has('stayEnd')) {
      newParams.delete('stayEnd');
      changed = true;
    }

    if (mode !== 'browse') {
      if (newParams.get('mode') !== mode) {
        newParams.set('mode', mode);
        changed = true;
      }
    } else if (newParams.has('mode')) {
      newParams.delete('mode');
      changed = true;
    }

    if (changed) {
      setSearchParams(newParams, { replace: true });
    }
  }, [stayDates, mode, searchParams, setSearchParams]);

  // Computed values
  const hasValidStayDates = useMemo(() => {
    if (!stayDates.startDate || !stayDates.endDate) return false;

    const start = parseISO(stayDates.startDate);
    const end = parseISO(stayDates.endDate);

    return isValid(start) && isValid(end) && isBefore(start, end);
  }, [stayDates]);

  const daysUntilStayEnds = useMemo(() => {
    if (!stayDates.endDate) return null;

    const end = parseISO(stayDates.endDate);
    if (!isValid(end)) return null;

    const today = startOfDay(new Date());
    const days = differenceInDays(end, today);

    return days >= 0 ? days : null;
  }, [stayDates.endDate]);

  const stayDuration = useMemo(() => {
    if (!hasValidStayDates) return null;

    const start = parseISO(stayDates.startDate!);
    const end = parseISO(stayDates.endDate!);

    return differenceInDays(end, start);
  }, [hasValidStayDates, stayDates]);

  // Actions
  const setStayDates = useCallback(
    (startDate: string | null, endDate: string | null) => {
      setStayDatesState({
        startDate: isValidDateString(startDate) ? startDate : null,
        endDate: isValidDateString(endDate) ? endDate : null,
      });
    },
    []
  );

  const setMode = useCallback((newMode: WidgetMode) => {
    setModeState(newMode);
  }, []);

  const clearStayDates = useCallback(() => {
    setStayDatesState({ startDate: null, endDate: null });
    setModeState('browse');
  }, []);

  const value = useMemo(
    () => ({
      stayDates,
      mode,
      hasValidStayDates,
      daysUntilStayEnds,
      stayDuration,
      setStayDates,
      setMode,
      clearStayDates,
    }),
    [
      stayDates,
      mode,
      hasValidStayDates,
      daysUntilStayEnds,
      stayDuration,
      setStayDates,
      setMode,
      clearStayDates,
    ]
  );

  return <StayContext.Provider value={value}>{children}</StayContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStayContext() {
  const context = useContext(StayContext);
  if (!context) {
    throw new Error('useStayContext must be used within a StayProvider');
  }
  return context;
}

/**
 * Hook to check if we're in during-stay mode with valid dates
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDuringStayMode() {
  const { mode, hasValidStayDates, daysUntilStayEnds, stayDates } = useStayContext();

  return {
    isDuringStayMode: mode === 'during-stay' && hasValidStayDates,
    daysUntilStayEnds,
    stayStartDate: stayDates.startDate,
    stayEndDate: stayDates.endDate,
  };
}
